import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { db } from "services/firebase";
import BarChartPlot from "components/plots/BarChartPlot";
import ScatterRatingPlot from "components/plots/ScatterRatingPlot";

const CHART_HEIGHT_PX = 124;

// Canonical step order + display labels
const STEP_ORDER = [
  "record",
  "diagnosis",
  "diagnostic_plan",
  "confirm_diagnosis",
  "treatment",
  "discharge",
  "chart",
];
const STEP_DISPLAY = {
  record: "Record",
  diagnosis: "Diagnosis",
  diagnostic_plan: "Diag. Plan",
  confirm_diagnosis: "Confirm Dx",
  treatment: "Treatment",
  discharge: "Discharge",
  chart: "Chart",
};

function buildAvailableMonths(count = 6) {
  const months = [];
  const now = new Date();

  for (let i = 0; i < count; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    months.push({
      value,
      label: date.toLocaleDateString("en-US", { year: "numeric", month: "long" }),
    });
  }

  return months;
}

function getDateParts(yearMonth) {
  const [year, month] = String(yearMonth).split("-").map(Number);
  return { year, month, daysInMonth: new Date(year, month, 0).getDate() };
}

function buildDayLabels(yearMonth) {
  if (!yearMonth) return [];
  const { year, month, daysInMonth } = getDateParts(yearMonth);
  return Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    return {
      label: String(day),
      dateKey: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    };
  });
}

const GraphMonthlyUsageChartmind = ({ organizationId }) => {
  const theme = useTheme();
  const availableMonths = useMemo(() => buildAvailableMonths(6), []);
  const [selectedMonth, setSelectedMonth] = useState(availableMonths[0]?.value || "");
  const [usageMetric, setUsageMetric] = useState("sessions");

  // Daily docs for the selected month — up to 31 docs regardless of org size
  const [dailyDocs, setDailyDocs] = useState([]);
  const [loading, setLoading] = useState(Boolean(organizationId));
  const [error, setError] = useState("");

  // Satisfaction (separate collection, unchanged)
  const [satisfactionPoints, setSatisfactionPoints] = useState([]);
  const [satisfactionLoading, setSatisfactionLoading] = useState(false);
  const [satisfactionError, setSatisfactionError] = useState("");

  useEffect(() => {
    if (!organizationId || !selectedMonth) {
      setDailyDocs([]);
      setLoading(false);
      setError("");
      return undefined;
    }

    let cancelled = false;

    const loadDailyDocs = async () => {
      try {
        setLoading(true);
        setError("");

        const runQuery = () =>
          db
            .collection("organizations")
            .doc(organizationId)
            .collection("chartmind_daily")
            .where("yearMonth", "==", selectedMonth)
            .get();

        let snapshot;
        try {
          snapshot = await runQuery();
        } catch (firstErr) {
          if (firstErr.code !== "permission-denied") throw firstErr;
          await new Promise((r) => setTimeout(r, 1200));
          snapshot = await runQuery();
        }

        if (cancelled) return;

        const docs = [];
        snapshot.forEach((doc) => {
          const data = doc.data() || {};
          docs.push({ dateKey: data.dateKey || doc.id, steps: data.steps || {} });
        });

        setDailyDocs(docs);
      } catch (loadError) {
        if (cancelled) return;
        console.error("[GraphMonthlyUsageChartmind] Failed to load daily stats:", loadError);
        setError(loadError.message || "Failed to load ChartMind usage.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDailyDocs();
    return () => { cancelled = true; };
  }, [organizationId, selectedMonth]);

  useEffect(() => {
    if (usageMetric !== "satisfaction" || !organizationId || !selectedMonth) {
      setSatisfactionPoints([]);
      setSatisfactionError("");
      return undefined;
    }

    let cancelled = false;

    const loadSatisfaction = async () => {
      try {
        setSatisfactionLoading(true);
        setSatisfactionError("");

        const snapshot = await db
          .collection("organizations")
          .doc(organizationId)
          .collection("chartmind_satisfaction_ratings")
          .where("yearMonth", "==", selectedMonth)
          .get();

        if (cancelled) return;

        const points = [];
        snapshot.forEach((doc) => {
          const data = doc.data() || {};
          if (data.rating == null || data.dayOfMonth == null) return;
          points.push({ day: Number(data.dayOfMonth), rating: Number(data.rating) });
        });

        setSatisfactionPoints(points);
      } catch (err) {
        if (cancelled) return;
        console.error("[GraphMonthlyUsageChartmind] Failed to load satisfaction data:", err);
        setSatisfactionError(err.message || "Failed to load satisfaction data.");
      } finally {
        if (!cancelled) setSatisfactionLoading(false);
      }
    };

    loadSatisfaction();
    return () => { cancelled = true; };
  }, [organizationId, selectedMonth, usageMetric]);

  // Daily session-start counts (step "record") keyed by dateKey
  const dailyCompletionsByDate = useMemo(() => {
    const map = {};
    dailyDocs.forEach(({ dateKey, steps }) => {
      if (steps.record) map[dateKey] = steps.record;
    });
    return map;
  }, [dailyDocs]);

  // Monthly per-step totals across all daily docs (for funnel view)
  const stepCounts = useMemo(() => {
    const map = {};
    dailyDocs.forEach(({ steps }) => {
      Object.entries(steps).forEach(([step, count]) => {
        map[step] = (map[step] || 0) + count;
      });
    });
    return map;
  }, [dailyDocs]);

  const dayEntries = useMemo(() => buildDayLabels(selectedMonth), [selectedMonth]);

  const sessionsSeries = useMemo(() => ({
    labels: dayEntries.map((d) => d.label),
    data: dayEntries.map((d) => dailyCompletionsByDate[d.dateKey] || 0),
  }), [dayEntries, dailyCompletionsByDate]);

  // Only show steps that have at least one event, in canonical order
  const funnelSteps = useMemo(
    () => STEP_ORDER.filter((s) => stepCounts[s] != null),
    [stepCounts],
  );
  const funnelSeries = useMemo(() => ({
    labels: funnelSteps.map((s) => STEP_DISPLAY[s] || s),
    data: funnelSteps.map((s) => stepCounts[s] || 0),
  }), [funnelSteps, stepCounts]);

  const selectedMonthLabel = availableMonths.find((m) => m.value === selectedMonth)?.label || selectedMonth;

  const sessionsTooltipTitle = useCallback(
    (context) => `${selectedMonthLabel} ${context[0]?.label || ""}`,
    [selectedMonthLabel],
  );
  const sessionsTooltipLabel = useCallback(
    (context) => `${context.parsed.y} ChartMind session${context.parsed.y === 1 ? "" : "s"} started`,
    [],
  );
  const funnelTooltipLabel = useCallback(
    (context) => `${context.parsed.y} user${context.parsed.y === 1 ? "" : "s"} reached this step`,
    [],
  );

  if (!organizationId) return null;

  const isSessionsMetric = usageMetric === "sessions";
  const isFunnelMetric = usageMetric === "funnel";

  return (
    <Paper id="chartmind-monthly-usage" sx={{ mt: 3, p: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          gap: 2,
          mb: 1.5,
        }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary">
            ChartMind Usage
          </Typography>
          <Typography variant="h6">
            {isSessionsMetric && "Started sessions for your organization"}
            {isFunnelMetric && "Step funnel for your organization"}
            {usageMetric === "satisfaction" && "Session satisfaction"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSessionsMetric && "Daily count of users who began a ChartMind session."}
            {isFunnelMetric && "How many times each step was reached this month."}
            {usageMetric === "satisfaction" && "Average satisfaction score per day."}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select value={usageMetric} onChange={(e) => setUsageMetric(e.target.value)}>
              <MenuItem value="sessions">Sessions</MenuItem>
              <MenuItem value="funnel">Funnel</MenuItem>
              <MenuItem value="satisfaction">Satisfaction</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
              {availableMonths.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {isSessionsMetric && (
        <BarChartPlot
          labels={sessionsSeries.labels}
          data={sessionsSeries.data}
          loading={loading}
          error={error}
          height={CHART_HEIGHT_PX}
          color={theme.palette.primary.main}
          datasetLabel="Started ChartMind sessions"
          emptyMessage="No ChartMind sessions recorded for this month yet."
          tooltipTitle={sessionsTooltipTitle}
          tooltipLabel={sessionsTooltipLabel}
          xAxisTitle="Day of month"
          yAxisTitle="Sessions"
        />
      )}

      {isFunnelMetric && (
        <BarChartPlot
          labels={funnelSeries.labels}
          data={funnelSeries.data}
          loading={loading}
          error={error}
          height={CHART_HEIGHT_PX}
          color={theme.palette.secondary.main}
          datasetLabel="Times step was reached"
          emptyMessage="No step events recorded for this month yet."
          tooltipLabel={funnelTooltipLabel}
          xAxisTitle="Step"
          yAxisTitle="Times reached"
        />
      )}

      {usageMetric === "satisfaction" && (
        <ScatterRatingPlot
          points={satisfactionPoints}
          daysInMonth={selectedMonth ? getDateParts(selectedMonth).daysInMonth : 31}
          loading={satisfactionLoading}
          error={satisfactionError}
          height={CHART_HEIGHT_PX}
          emptyMessage="No satisfaction ratings recorded for this month yet."
        />
      )}
    </Paper>
  );
};

export default GraphMonthlyUsageChartmind;
